<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("promo_notification.title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <div class="row">
        <!-- Compose form -->
        <div class="col-lg-5">
          <card>
            <template slot="header">
              <h3 class="mb-0">{{ $t("promo_notification.compose") }}</h3>
              <p class="text-sm text-muted mb-0">{{ $t("promo_notification.compose_hint") }}</p>
            </template>

            <div v-loading="sending">
              <base-input
                v-model="form.title"
                :label="$t('promo_notification.field_title')"
                :placeholder="$t('promo_notification.field_title')"
              ></base-input>

              <!-- Icon: URL + upload -->
              <label class="form-control-label">{{ $t("promo_notification.field_icon") }}</label>
              <div class="d-flex align-items-center mb-3">
                <img
                  v-if="form.icon_url"
                  :src="form.icon_url"
                  alt=""
                  class="rounded mr-2"
                  style="width: 44px; height: 44px; object-fit: cover;"
                />
                <base-input
                  v-model="form.icon_url"
                  class="flex-grow-1 mb-0"
                  :placeholder="$t('promo_notification.url_placeholder')"
                ></base-input>
                <base-button size="sm" type="secondary" class="ml-2" @click="pickIcon">
                  <i class="fa fa-upload"></i>
                </base-button>
                <input ref="iconInput" type="file" accept="image/*" class="d-none" @change="onIconChange" />
              </div>

              <base-input :label="$t('promo_notification.field_body')">
                <textarea
                  v-model="form.body"
                  class="form-control"
                  rows="4"
                  :placeholder="$t('promo_notification.field_body')"
                ></textarea>
              </base-input>

              <!-- Body image: URL + upload -->
              <label class="form-control-label">{{ $t("promo_notification.field_image") }}</label>
              <div class="d-flex align-items-center mb-3">
                <img
                  v-if="form.image_url"
                  :src="form.image_url"
                  alt=""
                  class="rounded mr-2"
                  style="width: 44px; height: 44px; object-fit: cover;"
                />
                <base-input
                  v-model="form.image_url"
                  class="flex-grow-1 mb-0"
                  :placeholder="$t('promo_notification.url_placeholder')"
                ></base-input>
                <base-button size="sm" type="secondary" class="ml-2" @click="pickImage">
                  <i class="fa fa-upload"></i>
                </base-button>
                <input ref="imageInput" type="file" accept="image/*" class="d-none" @change="onImageChange" />
              </div>

              <!-- Link -->
              <div class="row">
                <div class="col-md-5">
                  <base-input :label="$t('promo_notification.field_link_type')">
                    <el-select v-model="form.link_type" clearable class="w-100" :placeholder="$t('promo_notification.link_none')">
                      <el-option :label="$t('promo_notification.link_product')" value="product"></el-option>
                      <el-option :label="$t('promo_notification.link_coupon')" value="coupon"></el-option>
                    </el-select>
                  </base-input>
                </div>
                <div class="col-md-7">
                  <base-input
                    v-model="form.link_ref"
                    :label="linkRefLabel"
                    :placeholder="linkRefLabel"
                    :disabled="!form.link_type"
                  ></base-input>
                </div>
              </div>

              <div class="d-flex align-items-center justify-content-between mt-2">
                <span class="text-sm text-muted">
                  <i class="fa fa-users"></i> {{ $t("promo_notification.audience_all") }}
                </span>
                <!-- spec 2026-10-07-bo-form-draft-clear-design.md §4 A12: clear sits
                     with the send button (this page has no header buttons) -->
                <div>
                  <form-clear-button class="mr-2" :disabled="sending" @click="clearForm" />
                  <base-button type="primary" :disabled="sending" @click="send">
                    <i class="fa fa-paper-plane"></i> {{ $t("promo_notification.send") }}
                  </base-button>
                </div>
              </div>
            </div>
          </card>
        </div>

        <!-- Sent list -->
        <div class="col-lg-7">
          <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
            <template slot="header">
              <h3 class="mb-0">{{ $t("promo_notification.sent_list") }}</h3>
            </template>

            <div v-loading="loading">
              <el-table :data="rows" row-key="id" header-row-class-name="thead-light">
                <el-table-column :label="$t('promo_notification.col_title')" min-width="180">
                  <template slot-scope="{ row }">
                    <div class="d-flex align-items-center">
                      <img
                        v-if="row.icon_url || row.image_url"
                        :src="row.icon_url || row.image_url"
                        alt=""
                        class="rounded mr-2"
                        style="width: 32px; height: 32px; object-fit: cover;"
                      />
                      <div>
                        <div class="font-weight-bold">{{ row.title }}</div>
                        <small class="text-muted">{{ row.body }}</small>
                      </div>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('promo_notification.col_link')" min-width="120" align="center">
                  <template slot-scope="{ row }">
                    <span v-if="row.link_type" class="badge badge-info">
                      {{ row.link_type }}<span v-if="row.link_ref">: {{ row.link_ref }}</span>
                    </span>
                    <span v-else class="text-muted">-</span>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('promo_notification.col_recipients')" min-width="90" align="center">
                  <template slot-scope="{ row }">{{ row.recipient_count != null ? row.recipient_count : "-" }}</template>
                </el-table-column>
                <el-table-column :label="$t('promo_notification.col_sent_at')" min-width="140">
                  <template slot-scope="{ row }">{{ formatDate(row.created_at) }}</template>
                </el-table-column>
              </el-table>
            </div>

            <div slot="footer" class="d-flex align-items-center justify-content-between">
              <p class="text-muted mb-0">{{ $t("total") }}: {{ total }}</p>
              <base-pagination
                v-model="page"
                :per-page="perPage"
                :total="total"
                @input="loadData"
              ></base-pagination>
            </div>
          </card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import swal from "sweetalert2";
import { NotificationService, AppCatalogUploadService } from "@/resource";
import formDraft from "@/mixins/formDraft";
import FormClearButton from "@/components/FormClearButton.vue";

// #169 — BO admin: compose + broadcast a promo notification (audience: all) and
// review previously-sent broadcasts. Uses NotificationService (/api/v3/
// notifications). Image uploads reuse the catalog presign flow with the
// already-allowlisted "product" object_type (avoids the cdn-worker
// ALLOWED_PREFIXES gotcha for a brand-new prefix).
export default {
  name: "PromoNotificationAdmin",
  mixins: [formDraft],
  components: {
    FormClearButton,
    RouteBreadCrumb,
    BasePagination,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  data() {
    return {
      loading: false,
      sending: false,
      rows: [],
      total: 0,
      page: 1,
      perPage: 20,
      form: this.emptyForm(),
    };
  },
  computed: {
    linkRefLabel() {
      if (this.form.link_type === "product") return this.$t("promo_notification.link_ref_product");
      if (this.form.link_type === "coupon") return this.$t("promo_notification.link_ref_coupon");
      return this.$t("promo_notification.field_link_ref");
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    emptyForm() {
      return {
        title: "",
        icon_url: "",
        body: "",
        image_url: "",
        link_type: "",
        link_ref: "",
      };
    },
    // formDraft contract (spec 2026-10-07-bo-form-draft-clear-design.md §3 F1,
    // §4 A12): the compose card only — title, icon, body, image, link type/ref
    // back to emptyForm() and both hidden file inputs emptied. The sent list
    // (rows/page/total) is not form input and stays.
    resetForm() {
      this.form = this.emptyForm();
      ["iconInput", "imageInput"].forEach((r) => {
        const el = this.$refs[r];
        if (el) el.value = "";
      });
    },
    async loadData() {
      this.loading = true;
      try {
        const res = await NotificationService.list({ page: this.page, per_page: this.perPage });
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.rows = d.items || [];
        this.total = d.total || 0;
      } catch (err) {
        swal.fire({
          title: (err.response && err.response.data && err.response.data.Cause) || err.message,
          icon: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    pickIcon() {
      this.$refs.iconInput.value = "";
      this.$refs.iconInput.click();
    },
    pickImage() {
      this.$refs.imageInput.value = "";
      this.$refs.imageInput.click();
    },
    onIconChange(e) {
      this.uploadTo(e, "icon_url");
    },
    onImageChange(e) {
      this.uploadTo(e, "image_url");
    },
    async uploadTo(e, field) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;
      const maxBytes = 5 * 1024 * 1024;
      if (file.size > maxBytes) {
        this.$notify({ type: "danger", message: this.$t("errors.upload.file_too_large") || "File too large (max 5MB)" });
        return;
      }
      // The form object this upload belongs to; a clear (resetForm) or a
      // successful send swaps `form`, and a late upload must not write into
      // the new empty one (verifier advisory 2026-10-07).
      const target = this.form;
      try {
        const presignRes = await AppCatalogUploadService.presign({
          object_type: "product",
          file_name: file.name,
          content_type: file.type,
        });
        const { upload_url, cdn_url } = presignRes.data;
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", upload_url);
          xhr.setRequestHeader("Content-Type", file.type);
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error("Upload failed: " + xhr.status));
          };
          xhr.onerror = () => reject(new Error("Network error"));
          xhr.send(file);
        });
        if (this.form !== target) return;
        this.form[field] = cdn_url;
      } catch (err) {
        const detail = err && err.message ? ": " + err.message : "";
        this.$notify({ type: "danger", message: (this.$t("errors.upload.presign_failed") || "Upload failed") + detail });
      }
    },
    async send() {
      if (!this.form.title || !this.form.title.trim()) {
        this.$notify({ type: "warning", message: this.$t("promo_notification.err_title_required") });
        return;
      }
      if (this.form.link_type && !this.form.link_ref) {
        this.$notify({ type: "warning", message: this.$t("promo_notification.err_link_ref_required") });
        return;
      }
      const confirm = await swal.fire({
        title: this.$t("promo_notification.confirm_title"),
        text: this.$t("promo_notification.confirm_body"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("promo_notification.send"),
        cancelButtonText: this.$t("cancel") || "Cancel",
      });
      if (!confirm.value) return;

      this.sending = true;
      try {
        const payload = {
          title: this.form.title.trim(),
          icon_url: this.form.icon_url || "",
          body: this.form.body || "",
          image_url: this.form.image_url || "",
          link_type: this.form.link_type || "",
          link_ref: this.form.link_ref || "",
          audience: "all",
        };
        await NotificationService.create(payload);
        this.$notify({ type: "success", message: this.$t("promo_notification.sent_ok") });
        this.afterSaveReset();
        this.page = 1;
        this.loadData();
      } catch (err) {
        swal.fire({
          title: (err.response && err.response.data && err.response.data.Cause) || err.message,
          icon: "error",
        });
      } finally {
        this.sending = false;
      }
    },
    formatDate(v) {
      if (!v) return "-";
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      return d.toLocaleString();
    },
  },
};
</script>
